import { useState, useCallback, useEffect, useRef } from "react";

/* ── Icons (14x14, stroke="currentColor") ── */

function ClipboardIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="2" width="6" height="4" rx="1" />
      <path d="M9 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2h-2" />
    </svg>
  );
}

function CheckSmallIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="cx-text-accent">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

function RetryIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M3 21v-5h5" />
    </svg>
  );
}

function ThumbUpIcon({ filled }: { filled?: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 10v12" />
      <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z" />
    </svg>
  );
}

function ThumbDownIcon({ filled }: { filled?: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 14V2" />
      <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z" />
    </svg>
  );
}

/* ── MessageActions ── */

type Feedback = "positive" | "negative" | null;

interface MessageActionsProps {
  content: string;
  onRetry?: () => void;
  onEdit?: () => void;
  showFeedback?: boolean;
}

export function MessageActions({ content, onRetry, onEdit, showFeedback }: MessageActionsProps) {
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    timerRef.current = setTimeout(() => setCopied(false), 2000);
  }, [content]);

  function handleFeedback(value: "positive" | "negative") {
    setFeedback((prev) => (prev === value ? null : value));
  }

  return (
    <div className="flex gap-0.5">
      {onEdit && (
        <button className="cx-msg-action" onClick={onEdit} aria-label="Edit" data-tooltip="Edit">
          <PencilIcon />
        </button>
      )}
      <button className="cx-msg-action" onClick={handleCopy} aria-label="Copy" data-tooltip={copied ? "Copied" : "Copy"}>
        {copied ? <CheckSmallIcon /> : <ClipboardIcon />}
      </button>
      {onRetry && (
        <button className="cx-msg-action" onClick={onRetry} aria-label="Retry" data-tooltip="Retry">
          <RetryIcon />
        </button>
      )}
      {showFeedback && (
        <>
          <button
            className={`cx-msg-action${feedback === "positive" ? " cx-msg-action--active" : ""}`}
            onClick={() => handleFeedback("positive")}
            aria-label="Good response"
            data-tooltip="Good"
          >
            <ThumbUpIcon filled={feedback === "positive"} />
          </button>
          <button
            className={`cx-msg-action${feedback === "negative" ? " cx-msg-action--active" : ""}`}
            onClick={() => handleFeedback("negative")}
            aria-label="Bad response"
            data-tooltip="Bad"
          >
            <ThumbDownIcon filled={feedback === "negative"} />
          </button>
        </>
      )}
    </div>
  );
}
